// @ts-nocheck
import { SortableHeader } from "@/components/shared/SortableHeader";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import { Plus, Trash2, Loader2, Download } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { BulkActionBar, type BulkAction } from "@/components/shared/BulkActionBar";
import { useRowSelection } from "@/hooks/useRowSelection";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { exportToExcel } from "@/lib/excel.utils";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ListPageShell,
  useListFilter,
} from "@/components/shared/ListPageShell";
import {
  PageFilterTags,
  type PageFilterTagItem,
} from "@/components/shared/PageFilterTags";
import { useConfirm } from "@/providers/ConfirmProvider";
import { MaterialTypeDetailDrawer } from "./MaterialTypeDetailDrawer";
import { MaterialTypeFormModal } from "./MaterialTypeFormModal";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import {
  InlineFilterRow,
  type InlineFilterDefinition,
} from "@/components/shared/InlineFilterRow";
import { downloadExcelTemplate, type ExcelColumnDef } from "@/lib/excel.utils";
import { ImportDialog } from "@/components/shared/ImportDialog";
import { useImportRefs } from "@/hooks/useImportRefs";
import { findByCode, runUpsertImport } from "@/lib/import-kit";
import { useExcelExport } from "@/hooks/useExcelExport";

/** Cột Excel loại vật tư — dùng chung xuất / file mẫu / nhập (05/11/2026); Nhóm, Loại vật liệu khớp danh mục Cấu hình */
const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "code", label: "Mã Loại Vật Tư", required: true },
  { key: "label", label: "Tên Hiển Thị" },
  { key: "group", label: "Nhóm Vật Tư", ref: "materialGroup" },
  { key: "material", label: "Loại Vật Liệu", ref: "materialSubtype" },
  { key: "gsm", label: "Định Lượng (GSM)", type: "number", min: 0 },
];

const FILTER_FIELDS: InlineFilterDefinition[] = [
  { key: "code", placeholder: "Mã code...", type: "text" },
  { key: "label", placeholder: "Tên hiển thị...", type: "text" },
  { key: "group", placeholder: "Nhóm...", type: "text" },
  { key: "material", placeholder: "Vật liệu...", type: "text" },
  { key: "gsm", placeholder: "Định lượng...", type: "text" },
  { key: "actions", type: "none" },
];

export default function MaterialTypeList() {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [viewingItem, setViewingItem] = useState<any | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Fetch
  const { data, isLoading, error } = useQuery({
    queryKey: ["material-types"],
    queryFn: async () => await trpcClient.config.getMaterialTypes.query(),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      setDeletingId(id);
      return await trpcClient.config.deleteMaterialType.mutate({ id });
    },
    onSuccess: async (_, id) => {
      // 1. Optimistic cache eviction (0ms)
      queryClient.setQueryData(["material-types"], (old: any[] | undefined) =>
        old ? old.filter((item: any) => item.id !== id) : []
      );
      setViewingItem((prev: any) => (prev?.id === id ? null : prev));
      // 2. Synchronize all queries
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ["material-types"] }),
        queryClient.invalidateQueries({ queryKey: ["material-types"] }),
        queryClient.invalidateQueries({ queryKey: ["material-types-lookup"] }),
        queryClient.invalidateQueries({ queryKey: ["config", "materialTypes"] }),
        queryClient.invalidateQueries({ queryKey: ["combobox"] }),
      ]);
      toast.success("Xóa loại vật tư thành công");
    },
    onError: (e: any) => {
      const raw = e?.message || "";
      if (raw.includes("foreign key") || raw.includes("related") || raw.includes("constraint")) {
        toast.error("Không thể xóa loại vật tư này vì đang được sử dụng bởi vật tư khác.");
      } else {
        toast.error(e?.message || "Lỗi khi xóa loại vật tư. Vui lòng thử lại.");
      }
    },
    onSettled: () => {
      setDeletingId(null);
    },
  });

  const allItems = data || [];
  const [selectedGroups, setSelectedGroups] = useState<string[] | null>(null);
  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  // Compute group counts for filter tags
  const groupTags = useMemo<PageFilterTagItem[]>(() => {
    const counts: Record<string, number> = {};
    allItems.forEach((item: any) => {
      const grp = item.group || "Chưa phân nhóm";
      counts[grp] = (counts[grp] || 0) + 1;
    });
    return Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, count]) => ({ key, label: key, count }));
  }, [allItems]);

  const preFilter = useCallback((item: any) => {
    if (selectedGroups && selectedGroups.length > 0) {
      const grp = item.group || "Chưa phân nhóm";
      if (!selectedGroups.includes(grp)) return false;
    }
    if (filterValues.code && !item.code?.toLowerCase().includes(filterValues.code.toLowerCase())) {
      return false;
    }
    if (filterValues.label && !item.label?.toLowerCase().includes(filterValues.label.toLowerCase())) {
      return false;
    }
    if (filterValues.group && !item.group?.toLowerCase().includes(filterValues.group.toLowerCase())) {
      return false;
    }
    if (filterValues.material && !item.material?.toLowerCase().includes(filterValues.material.toLowerCase())) {
      return false;
    }
    if (filterValues.gsm && !String(item.gsm || "").toLowerCase().includes(filterValues.gsm.toLowerCase())) {
      return false;
    }
    return true;
  }, [selectedGroups, filterValues]);

  const {
    filtered: filteredMaterialTypes,
    paginatedItems,
    totalItems,
    currentPage,
    onPageChange,
    pageSize,
    onPageSizeChange,
    sortField,
    sortOrder,
    handleSort,
  } = useListFilter({
    data: allItems,
    searchFields: ["code", "group", "material", "label", "gsm"],
    preFilter,
  });

  const handleFilterChange = (key: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    onPageChange(1);
  };

  const handleClearAll = () => {
    setFilterValues({});
    setSelectedGroups(null);
    onPageChange(1);
  };

  const handleGroupSelect = useCallback((key: string) => {
    onPageChange(1);
    if (!key) {
      setSelectedGroups(null);
      return;
    }
    setSelectedGroups((prev) => {
      if (!prev) return [key];
      if (prev.includes(key)) {
        const next = prev.filter((k) => k !== key);
        return next.length === 0 ? null : next;
      }
      return [...prev, key];
    });
  }, [onPageChange]);

  const urlView = useRouterState({ select: (s) => (s.location.search as any)?.view }) as string | undefined;
  useEffect(() => {
    if (!urlView || viewingItem) return;
    const hit = allItems.find((i: any) => i.id === urlView || i.code === urlView);
    if (hit) setViewingItem(hit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlView, allItems]);

  const activeViewingItem = useMemo(() => {
    if (!viewingItem) return null;
    return allItems.find((i: any) => i.id === viewingItem.id) || viewingItem;
  }, [allItems, viewingItem]);

  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_loai_vat_tu",
    columns: EXPORT_COLUMNS,
    localData: filteredMaterialTypes,
  });

  // ── Chọn nhiều dòng + thao tác hàng loạt ──
  const { hasPermission } = useRoleAccess();
  // Nhập Excel (05/11/2026): mã đã có → cập nhật, mới → tạo; Nhóm / Loại vật liệu chưa có thì hỏi thêm; báo cáo từng dòng
  const [showImport, setShowImport] = useState(false);
  const importRefs = useImportRefs();
  const typeImportRefs = useMemo(() => ({ materialGroup: importRefs.materialGroup, materialSubtype: importRefs.materialSubtype }), [importRefs]);
  const handleImport = async (rows: Record<string, unknown>[]) => {
    const result = await runUpsertImport(rows, {
      codeOf: (row) => String(row.code ?? "").trim(),
      findExisting: findByCode(() => trpcClient.config.getMaterialTypes.query(), (t) => t.code),
      prepare: (row, mode) => {
        const { code, ...rest } = row as any;
        const data: Record<string, unknown> = { ...rest };
        if (data.gsm !== undefined) data.gsm = String(data.gsm);
        return mode === "create" ? { ...data, code } : data;
      },
      create: (data) => trpcClient.config.addMaterialType.mutate(data as any),
      update: (id, data) => trpcClient.config.updateMaterialType.mutate({ id, data } as any),
    });
    await queryClient.invalidateQueries({ queryKey: ["material-types"] });
    await queryClient.invalidateQueries({ queryKey: ["material-types-lookup"] });
    return result;
  };
  const rowIds = useMemo(() => paginatedItems.map((i: any) => i.id), [paginatedItems]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedCount, selectedIds, clearSelection } = rowSelection;
  const bulkActions: BulkAction[] = selectedCount
    ? [
        {
          icon: <Download className="h-4 w-4" />,
          label: "Xuất Excel",
          variant: "outline",
          onClick: () =>
            exportToExcel(
              filteredMaterialTypes.filter((i: any) => selectedIds.has(i.id)),
              EXPORT_COLUMNS,
              "loai_vat_tu_da_chon",
            ),
        },
        ...(hasPermission("material-types", "delete")
          ? [
              {
                icon: <Trash2 className="h-4 w-4" />,
                label: `Xoá (${selectedCount})`,
                variant: "destructive" as const,
                onClick: () =>
                  executeBulkAction({
                    confirmFn: confirm,
                    selectedIds: Array.from(selectedIds),
                    actionFn: async (id) => trpcClient.config.deleteMaterialType.mutate({ id }),
                    actionName: "xóa",
                    itemType: "loại vật tư",
                    onSuccess: () => {
                      clearSelection();
                      queryClient.invalidateQueries({ queryKey: ["material-types"] });
                      queryClient.invalidateQueries({ queryKey: ["combobox"] });
                    },
                    requireConfirm: true,
                    confirmMessage: `Xóa ${selectedCount} loại vật tư đã chọn?`,
                  }),
              },
            ]
          : []),
      ]
    : [];

  if (isLoading)
    return (
      <Card className="p-8 flex items-center justify-center">
        <Loader2 className="animate-spin text-indigo-600 mr-2" /> Đang tải...
      </Card>
    );
  if (error)
    return (
      <Card className="p-8 text-center text-red-500">Lỗi khi tải dữ liệu.</Card>
    );

  return (
    <ListPageShell
        trashModule="material-types"
      unitLabel="loại vật tư"
      filterFields={FILTER_FIELDS}
      filterValues={filterValues}
      showFilter={showFilter}
      onToggleFilter={() => setShowFilter((prev) => !prev)}
      onClearAll={handleClearAll}
      headerFilters={
        <PageFilterTags
          items={groupTags}
          selectedKeys={selectedGroups}
          onSelect={handleGroupSelect}
          hideEmpty={false}
          dropdownLabel="Nhóm"
        />
      }
      actions={
        <ExportImportButtons
          onExport={handleExport}
          exportLoading={isExporting}
          onImportClick={hasPermission("material-types", "create") ? () => setShowImport(true) : undefined}
        >
          <Button
            size="sm"
            className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
            onClick={() => {
              setEditingItem(null);
              setIsModalOpen(true);
            }}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Thêm mới
          </Button>
        </ExportImportButtons>
      }
      currentPage={currentPage}
      pageSize={pageSize}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
    >
      <Card className="p-0 border-slate-200 shadow-sm overflow-hidden bg-white">
        <Table className={rowSelection.selectionMode ? "apv-select-mode" : undefined}>
          <TableHeader className="bg-slate-50/50">
            <TableRow>
              <DataTableSelectAllCheckbox rowSelection={rowSelection} />
              <SortableHeader
                label="Mã Code"
                field="code"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="apv-col-code font-semibold text-xs py-3 w-[150px]"
              />
              <SortableHeader
                label="Tên hiển thị"
                field="label"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="font-semibold text-xs py-3"
              />
              <SortableHeader
                label="Nhóm (Group)"
                field="group"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="font-semibold text-xs py-3"
              />
              <SortableHeader
                label="Vật liệu"
                field="material"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="font-semibold text-xs py-3"
              />
              <SortableHeader
                label="Định lượng"
                field="gsm"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="font-semibold text-xs py-3"
              />
              <TableHead className="apv-col-actions w-[100px] text-right font-semibold text-xs py-3">
                Thao tác
              </TableHead>
            </TableRow>
            {showFilter && (
              <InlineFilterRow
                columns={[
                  null,
                  { key: 'code' },
                  { key: 'label' },
                  { key: 'group' },
                  { key: 'material' },
                  { key: 'gsm' },
                  null,
                ]}
                fields={FILTER_FIELDS}
                values={filterValues}
                onChange={handleFilterChange}
              />
            )}
          </TableHeader>
          <TableBody>
            {paginatedItems.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-slate-500"
                >
                  Chưa có dữ liệu.
                </TableCell>
              </TableRow>
            ) : (
              paginatedItems.map((item: any) => (
                <DataTableRow
                  key={item.id}
                  id={item.id}
                  rowSelection={rowSelection}
                  className="font-medium cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setViewingItem(item)}
                >
                  <DataTableRowCheckbox id={item.id} rowSelection={rowSelection} />
                  <TableCell className="apv-col-code font-semibold text-indigo-700 py-2">
                    {item.code}
                  </TableCell>
                  <TableCell className="text-slate-800 py-2">
                    {item.label || item.code}
                  </TableCell>
                  <TableCell className="text-slate-600 py-2">
                    {item.group || "—"}
                  </TableCell>
                  <TableCell className="text-slate-600 py-2">
                    {item.material || "—"}
                  </TableCell>
                  <TableCell className="text-slate-600 py-2">
                    {item.gsm || "—"}
                  </TableCell>
                  <TableCell
                    className="apv-col-actions text-right py-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={deletingId === item.id}
                        className="h-7 w-7 hover:text-red-600 focus:text-red-600 disabled:opacity-50"
                        onClick={async () => {
                          if (
                            await confirm(`Xóa định dạng vật tư ${item.code}?`)
                          )
                            deleteMutation.mutate(item.id);
                        }}
                      >
                        {deletingId === item.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-red-500" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </DataTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <MaterialTypeDetailDrawer
        item={activeViewingItem}
        open={!!activeViewingItem}
        onOpenChange={(open) => !open && setViewingItem(null)}
        onEdit={(item) => {
          setEditingItem(item);
          setIsModalOpen(true);
        }}
      />

      {isModalOpen && (
        <MaterialTypeFormModal
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingItem(null);
          }}
          onSuccess={() => {
            onPageChange(1);
          }}
          id={editingItem?.id}
          initialData={editingItem}
          mode={editingItem ? 'edit' : 'create'}
          breadcrumb={['Loại vật tư', editingItem ? 'Cập nhật' : 'Thêm mới']}
        />
      )}
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={bulkActions}
      />
      <ImportDialog
        open={showImport}
        onOpenChange={setShowImport}
        title="Import Loại Vật Tư"
        columns={EXPORT_COLUMNS}
        refs={typeImportRefs}
        onImport={handleImport}
        onDownloadTemplate={() => downloadExcelTemplate(EXPORT_COLUMNS, "loai_vat_tu")}
      />
    </ListPageShell>
  );
}
